import { type TableProps, Tag } from 'antd';

import { columnsCache } from '@client/utils/table/column.util';
import {
  columnFilter,
  type TColumnFilter,
} from '@client/utils/table/filter.util';
import { columnSorter } from '@client/utils/table/sorter.util';
import { indexColumn } from '@client/utils/table/table.util';
import { tsToLocaleDateTime } from '@common/utils';

import type { TFilters, TSorts } from '@client/hooks/useTable';
import type { ELocales, IRevenueEntry, ERevenueStatus } from '@common/types';
import type { JSX } from 'react';

type TArgs = {
  locale: ELocales;
  sortedInfo: TSorts;
  filteredInfo: TFilters;
  entities: IRevenueEntry[];
};

const STATUS_COLOR_MAP: Record<ERevenueStatus, string> = {
  PENDING: 'orange',
  AVAILABLE: 'green',
  PAID_OUT: 'blue',
};

export const columnsMetadata = (
  props: TArgs,
  filterIn: string[] = [],
): TableProps<IRevenueEntry>['columns'] => {
  const { locale, sortedInfo, entities, filteredInfo } = props;

  const { cached, updateCached } = columnsCache<IRevenueEntry>({
    sortedInfo,
    filteredInfo,
    entities,
    locale,
    filterIn,
  });

  if (cached) {
    return cached;
  }

  const columns = [
    indexColumn,
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      align: 'center' as const,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'status',
      ) as TColumnFilter<IRevenueEntry>),
      ...columnSorter(sortedInfo, 'status', 'status', 1),
      render: (status: ERevenueStatus): JSX.Element => (
        <Tag color={STATUS_COLOR_MAP[status]}>{status}</Tag>
      ),
    },
    {
      title: 'Gross Amount',
      dataIndex: 'grossAmount',
      key: 'grossAmount',
      width: 130,
      align: 'right' as const,
      concealable: true,
      ...columnSorter(sortedInfo, 'grossAmount', 'grossAmount', 2),
      render: (grossAmount: number): JSX.Element => <span>{grossAmount}</span>,
    },
    {
      title: 'Fee %',
      dataIndex: 'feePercent',
      key: 'feePercent',
      width: 90,
      align: 'right' as const,
      concealable: true,
      ...columnSorter(sortedInfo, 'feePercent', 'feePercent', 3),
      render: (feePercent: number): JSX.Element => <span>{feePercent}%</span>,
    },
    {
      title: 'Net Amount',
      dataIndex: 'netAmount',
      key: 'netAmount',
      width: 120,
      align: 'right' as const,
      concealable: true,
      ...columnSorter(sortedInfo, 'netAmount', 'netAmount', 4),
      render: (netAmount: number): JSX.Element => <span>{netAmount}</span>,
    },
    {
      title: 'Reference ID',
      dataIndex: 'referenceId',
      key: 'referenceId',
      concealable: true,
      render: (referenceId: string | null): JSX.Element => (
        <span>{referenceId ?? '—'}</span>
      ),
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'userId',
      ) as TColumnFilter<IRevenueEntry>),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      align: 'center' as const,
      concealable: true,
      ...columnSorter(sortedInfo, 'createdAt', 'createdAt', 5),
      render: (createdAt: Date): JSX.Element => (
        <div>{tsToLocaleDateTime(createdAt)}</div>
      ),
    },
  ];

  const result = filterIn.length
    ? columns.filter(
        (column) =>
          filterIn.includes(column?.dataIndex as string) ||
          column.dataIndex === 'idx',
      )
    : columns;

  return updateCached(result);
};
